import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import type { PagedResult, MaterialDto } from '@/types/materials';
import type {
  CreateJobWorkReceiptRequest, JobWorkOrderDto, JobWorkReceiptDto, JobWorkReceiptLineDto, JobWorkReceiptLineInput,
} from '@/types/jobWork';

const emptyLine: JobWorkReceiptLineInput = {
  materialId: '', receivedQty: 0, batchNo: null, mfgDate: null, expiryDate: null, unitCost: null,
};

/** The return leg — DC receive against a Released Job Work Order. Mirrors GrnPage's dialog+table
 * shape: each line's material defaults to the order's own output material, but is freely
 * overridable, covering both "same material code" and "converted material code" returns. */
export default function JobWorkReceiptPage() {
  const { t } = useTranslation('jobWork');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ['job-work-receipts'],
    queryFn: async () => (await apiClient.get<JobWorkReceiptDto[]>('/job-work-receipts')).data,
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['job-work-orders'],
    queryFn: async () => (await apiClient.get<JobWorkOrderDto[]>('/job-work-orders')).data,
  });
  // Only Released orders can take a receipt — the same gate the backend enforces.
  const receivableOrders = orders.filter((o) => o.status === 'Released');

  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<JobWorkReceiptDto | null>(null);
  const [orderId, setOrderId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [vendorDcNo, setVendorDcNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [lines, setLines] = useState<JobWorkReceiptLineInput[]>([{ ...emptyLine }]);
  const [error, setError] = useState<string | null>(null);

  const selectedOrder = receivableOrders.find((o) => o.id === orderId) ?? null;

  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const resetForm = () => {
    setOrderId('');
    setWarehouseId('');
    setVendorDcNo('');
    setVehicleNo('');
    setLines([{ ...emptyLine }]);
    setError(null);
  };

  // Defaults the warehouse to the order's own, and seeds one line pre-filled with the order's
  // expected output material at its planned qty — the common case of "same material code, full
  // qty" needs no further edits, but every field (including materialId itself) stays freely
  // overridable for a converted material code or a quantity variance.
  const selectOrder = (id: string) => {
    setOrderId(id);
    setError(null);
    const order = receivableOrders.find((o) => o.id === id);
    if (!order) return;
    setWarehouseId(order.warehouseId);
    setLines([{ materialId: order.outputMaterialId, receivedQty: order.plannedQty, batchNo: null, mfgDate: null, expiryDate: null, unitCost: null }]);
  };

  const updateLine = (idx: number, patch: Partial<JobWorkReceiptLineInput>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: CreateJobWorkReceiptRequest = {
        jobWorkOrderId: orderId, warehouseId, vendorDcNo: vendorDcNo || null, vehicleNo: vehicleNo || null,
        lines: lines.filter((l) => l.materialId && l.receivedQty > 0),
      };
      return apiClient.post('/job-work-receipts', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-work-receipts'] });
      queryClient.invalidateQueries({ queryKey: ['job-work-qc-tasks-pending'] });
      queryClient.invalidateQueries({ queryKey: ['job-work-putaway-tasks-pending'] });
      setOpen(false);
      resetForm();
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.[0];
      setError(message ?? t('receipt.genericError'));
    },
  });

  const columns: DataTableColumn<JobWorkReceiptDto>[] = [
    { field: 'receiptNumber', headerName: t('receipt.table.number') },
    { field: 'jobWorkOrderNumber', headerName: t('receipt.table.order'), filterable: true },
    { field: 'supplierName', headerName: t('receipt.table.vendor'), filterable: true },
    { field: 'warehouseName', headerName: t('receipt.table.warehouse'), filterable: true },
    { field: 'receiptDate', headerName: t('receipt.table.date'), render: (r) => formatDate(r.receiptDate) },
    { field: 'vendorDcNo', headerName: t('receipt.table.dcNo'), render: (r) => r.vendorDcNo ?? '—' },
    { field: 'lines', headerName: t('receipt.table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('receipt.table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <Button size="small" onClick={() => setViewing(r)}>
          {t('receipt.view')}
        </Button>
      ),
    },
  ];

  const lineColumns: DataTableColumn<JobWorkReceiptLineDto>[] = [
    { field: 'materialCode', headerName: t('receipt.table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'receivedQty', headerName: t('receipt.form.received') },
    { field: 'acceptedQty', headerName: t('receipt.form.accepted') },
    { field: 'rejectedQty', headerName: t('receipt.form.rejected') },
    { field: 'batchNo', headerName: t('receipt.form.batchNo'), render: (l) => l.batchNo ?? '—' },
    { field: 'expiryDate', headerName: t('receipt.table.expiryDate'), render: (l) => formatDate(l.expiryDate) },
    { field: 'qcStatus', headerName: t('receipt.table.qcStatus'), render: (l) => (l.qcStatus ? <StatusChip status={l.qcStatus} /> : '—') },
  ];

  return (
    <>
      <PageHeader
        title={t('receipt.title')}
        subtitle={t('receipt.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.JOB_WORK_EXECUTE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('receipt.add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={receipts} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="job-work-receipts" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('receipt.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <SearchableSelect
              label={t('receipt.form.order')} value={orderId} onChange={selectOrder} fullWidth
              helperText={receivableOrders.length === 0 ? t('receipt.noOrdersHint') : undefined}
              options={receivableOrders.map((o) => ({ value: o.id, label: `${o.orderNumber} — ${o.supplierName}` }))}
            />

            {selectedOrder && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {t('receipt.expectedHint', {
                  material: `${selectedOrder.outputMaterialCode} — ${selectedOrder.outputMaterialName}`,
                  qty: selectedOrder.plannedQty,
                })}
              </Alert>
            )}

            <GridContainer columns={12}>
              <GridItem xs={12} md={4}>
                <TextField label={t('receipt.form.vendorDcNo')} value={vendorDcNo} onChange={(e) => setVendorDcNo(e.target.value)} fullWidth />
              </GridItem>
              <GridItem xs={12} md={4}>
                <TextField label={t('receipt.form.vehicleNo')} value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} fullWidth />
              </GridItem>
            </GridContainer>

            <Divider />
            <Typography variant="subtitle2">{t('receipt.form.lines')}</Typography>

            {lines.map((line, idx) => (
              <GridContainer key={idx} columns={24} spacing={1}>
                <GridItem xs={24} md={8}>
                  <SearchableSelect
                    label={t('receipt.form.material')} value={line.materialId} size="small" fullWidth
                    onChange={(materialId) => updateLine(idx, { materialId })}
                    options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                  />
                </GridItem>
                <GridItem xs={12} md={4}>
                  <TextField
                    type="number" label={t('receipt.form.received')} value={line.receivedQty} size="small" fullWidth
                    onChange={(e) => updateLine(idx, { receivedQty: Number(e.target.value) })}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <TextField
                    label={t('receipt.form.batchNo')} value={line.batchNo ?? ''} size="small" fullWidth
                    onChange={(e) => updateLine(idx, { batchNo: e.target.value || null })}
                  />
                </GridItem>
                <GridItem xs={12} md={5}>
                  <TextField
                    type="number" label={t('receipt.form.unitCost')} value={line.unitCost ?? ''} size="small" fullWidth
                    onChange={(e) => updateLine(idx, { unitCost: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </GridItem>
                <GridItem xs={24} md={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'center' }, height: '100%' }}>
                    <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </GridItem>
              </GridContainer>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])} sx={{ alignSelf: 'flex-start' }}>
              {t('receipt.form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('receipt.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!orderId || !warehouseId || lines.some((l) => !l.materialId || l.receivedQty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('receipt.form.generate')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.receiptNumber ?? ''}
        subtitle={viewing ? `${viewing.jobWorkOrderNumber} — ${viewing.supplierName}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />
    </>
  );
}
